// SubscriptionGate.jsx — subscription paywall (read-only enforcement).
//
// Reads the practice's subscription status (via db-query → Cloud SQL) once after
// sign-in. If the subscription is inactive/expired, it puts the data layer into
// READ-ONLY mode (db.js setDbReadOnly) — the practice can still VIEW charts but
// can't create/edit/delete — and shows a banner with a Manage Billing link.
//
// SAFETY: fail-OPEN. Default is editable; it only flips to read-only on an
// explicitly inactive status. Any error (network, missing row, no practice) leaves
// the app fully editable so a transient glitch can never lock out a paying practice.
// Founder/dev accounts are whitelisted. Demo mode never mounts this (see App.jsx).

import { useState, useEffect } from 'react';
import { db, setDbReadOnly } from '../lib/db';
import { apiFetch, getCurrentUserInfo } from '../lib/api';
import { notify } from '../utils/notify';

// Accounts that are never gated, regardless of practice subscription_status.
const WHITELIST = ['xwalkerobx@gmail.com', 'drwalker@epiphanymd.com', 'security@epiphanymd.com'];

export default function SubscriptionGate({ practiceId }) {
  const [lapsed, setLapsed]   = useState(false);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDbReadOnly(false); // fail-open default — editable until proven inactive

    (async () => {
      try {
        const { email } = await getCurrentUserInfo();
        if (email && WHITELIST.includes(email.toLowerCase())) return; // never gate
      } catch { /* fall through — don't block on identity lookup failure */ }

      if (!practiceId) return; // no practice → fail-open

      try {
        const { data, error } = await db
          .from('practices')
          .select('subscription_status, trial_ends_at')
          .eq('id', practiceId)
          .maybeSingle();

        if (error || !data) return; // fail-open on any error / missing row

        const s = data.subscription_status;
        const active =
          s === 'active' ||
          (s === 'trial' && data.trial_ends_at && new Date(data.trial_ends_at) > new Date());

        if (!cancelled && !active) {
          setLapsed(true);
          setDbReadOnly(true);
        }
      } catch {
        /* fail-open */
      }
    })();

    return () => { cancelled = true; setDbReadOnly(false); };
  }, [practiceId]);

  const manageBilling = async () => {
    setOpening(true);
    try {
      const res = await apiFetch('create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.url) window.location.href = data.url;
      else notify('Could not open the billing portal. Try Settings → Subscription.', 'error');
    } catch (e) {
      notify('Could not open the billing portal: ' + e.message, 'error');
    } finally {
      setOpening(false);
    }
  };

  if (!lapsed) return null;

  return (
    <div style={{
      background: 'rgba(239,68,68,0.12)', borderBottom: '1px solid rgba(239,68,68,0.3)',
      color: 'var(--text-1)', padding: '10px 16px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 14, fontSize: 13, flexWrap: 'wrap',
    }}>
      <span style={{ fontWeight: 700, color: '#f87171' }}>⚠ Subscription inactive</span>
      <span style={{ color: 'var(--text-2)' }}>
        The app is in read-only mode — you can view charts but can't make changes until billing is updated.
      </span>
      <button
        onClick={manageBilling}
        disabled={opening}
        style={{
          padding: '6px 14px', background: 'var(--teal)', color: '#fff', border: 'none',
          borderRadius: 7, cursor: opening ? 'default' : 'pointer', fontSize: 12, fontWeight: 600,
        }}
      >
        {opening ? 'Opening…' : 'Manage Billing'}
      </button>
    </div>
  );
}
